import { useCallback, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Box, Chip, Stack, Typography } from '@mui/material';

import { useReactTable, SortingState, getCoreRowModel, getSortedRowModel, ColumnDef, Row } from '@tanstack/react-table';
import { addWeeks, format, getMonth, getYear, isWithinInterval, parseISO } from 'date-fns';
import _keyBy from 'lodash/keyBy';

import { Constants, formatUserDateFromString, shortMonthMap } from '@arrived/common';
import {
  AccountOfferingBalance,
  isLeaseStatusRented,
  LeaseStatus,
  LEASE_STATUS_MESSAGES_STRINGS,
  OfferingId,
  UsageType,
} from '@arrived/models';

import { propertyNameAccessor, Table } from '../../../Table';
import { NEXT_DIVIDEND_DATE, VALID_DIVIDEND_DATE } from './Constants';
import { OfferingDialog } from './OfferingDialog';
import { StyledOfferingTableContainer } from './OfferingTableContainer.styles';

import { NavigationBarContext } from '../../../navigation';
import { PortfolioEquityTooltip, PrivacyContextAwareMoneyValue } from '../../common';
import {
  FirstDividendDate,
  MonthsOwnedCell,
  CellBodyTypography,
  EquityMetric,
  OfferingInfoCell,
  RentalIncomePercentMetric,
} from '../../common/cells';

const CURRENT_DATE = new Date();
const FORMATTED_NEXT_DIVIDEND_DATE = `${shortMonthMap.get(getMonth(NEXT_DIVIDEND_DATE) + 1)} ${getYear(
  NEXT_DIVIDEND_DATE
)}`;

const SHOW_NEXT_DIVIDEND_DATE = isWithinInterval(CURRENT_DATE, {
  start: VALID_DIVIDEND_DATE,
  end: NEXT_DIVIDEND_DATE,
});

export interface OfferingColumnType extends AccountOfferingBalance {
  isUpdatedRecently: boolean;
  ownershipStartDateParsed: Date;
}

const columns: ColumnDef<OfferingColumnType>[] = [
  {
    header: () => <FormattedMessage id="property-name" defaultMessage="Property Name" />,
    id: 'name',
    accessorFn: (row) => propertyNameAccessor(row.name),
    cell: ({
      row: {
        original: { imgUrl, name },
      },
    }) => <OfferingInfoCell imgSize={56} name={name} imgUrl={imgUrl} />,
    size: 100,
  },
  {
    header: () => <FormattedMessage id="location" defaultMessage="Location" />,
    accessorKey: 'marketName',
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return <CellBodyTypography>{value}</CellBodyTypography>;
    },
    size: 100,
  },
  {
    header: () => <FormattedMessage id="rental-type" defaultMessage="Rental Type" />,
    accessorKey: 'assetSummary.usageType',
    cell: ({ getValue }) => {
      const value = getValue() as UsageType;
      let rentalTypeText = <FormattedMessage id="portfolio.rentalType.na" />;
      switch (value) {
        case UsageType.LONG_TERM_RENTAL:
          rentalTypeText = <FormattedMessage id="portfolio.rentalType.longTerm" />;
          break;
        case UsageType.SHORT_TERM_RENTAL:
          rentalTypeText = <FormattedMessage id="portfolio.rentalType.shortTerm" />;
          break;
      }
      return <CellBodyTypography>{rentalTypeText}</CellBodyTypography>;
    },
    size: 100,
  },
  {
    header: () => <FormattedMessage id="owner-since" defaultMessage="Owner Since" />,
    accessorKey: 'ownershipStartDateParsed',
    cell: ({ getValue }) => {
      const value = getValue() as Date;
      return (
        <>
          <CellBodyTypography>{format(value, 'MMM yyyy')}</CellBodyTypography>
          <CellBodyTypography>
            <MonthsOwnedCell startDate={value as Date} />
          </CellBodyTypography>
        </>
      );
    },
    invertSorting: true,
    size: 80,
  },
  {
    header: () => <FormattedMessage id="rental-status" defaultMessage="Rental Status" />,
    accessorKey: 'assetSummary.assetUtilizationStatus',
    cell: ({ getValue, row }) => {
      const value = getValue() as LeaseStatus;

      if (row.original.assetSummary.usageType === UsageType.SHORT_TERM_RENTAL) {
        return (
          <CellBodyTypography>
            <FormattedMessage id="variable" defaultMessage="Variable" />
          </CellBodyTypography>
        );
      }

      return (
        <CellBodyTypography>
          <FormattedMessage
            id={LEASE_STATUS_MESSAGES_STRINGS[value].display.id}
            defaultMessage={LEASE_STATUS_MESSAGES_STRINGS[value].display.text}
          />
          {isLeaseStatusRented(value) && (
            <Typography variant="body2">
              <FormattedMessage
                id="lease-ends"
                defaultMessage="(Lease Ends {date})"
                values={{
                  date: formatUserDateFromString(row.original.assetSummary.leaseDetails.leaseEndDate, 'MMM yyyy'),
                }}
              />
            </Typography>
          )}
        </CellBodyTypography>
      );
    },
    size: 110,
  },
  {
    header: () => <FormattedMessage id="portfolio.income.header" />,
    accessorKey: 'assetSummary.leaseDetails.currentRentAmount',
    cell: ({ getValue, row }) => {
      let passedValue = getValue() as number;
      const leaseStatus = row.original.assetSummary.assetUtilizationStatus;
      if (leaseStatus === LeaseStatus.VACANT_UNRENTED_READY)
        passedValue = row.original.assetSummary.leaseDetails.proformaRentAmount;
      const rentDiff =
        row.original.assetSummary.leaseDetails.currentRentAmount -
        row.original.assetSummary.leaseDetails.proformaRentAmount;
      return (
        <CellBodyTypography>
          {row.original.assetSummary.usageType === UsageType.SHORT_TERM_RENTAL ? (
            <FormattedMessage id="portfolio.rentalIncome.variable" />
          ) : (
            <PrivacyContextAwareMoneyValue length={3} value={passedValue} noDecimals />
          )}
          {isLeaseStatusRented(leaseStatus) && row.original.assetSummary.usageType === UsageType.LONG_TERM_RENTAL && (
            <Typography variant="body2">
              {rentDiff !== 0 ? (
                <FormattedMessage
                  id="portfolio.rentalIncome.forecast"
                  values={{
                    value: <PrivacyContextAwareMoneyValue length={3} value={rentDiff} noDecimals displaySign />,
                  }}
                />
              ) : (
                <FormattedMessage id="portfolio.rentalIncome.metForecast" />
              )}
            </Typography>
          )}
          {leaseStatus === LeaseStatus.VACANT_UNRENTED_READY && (
            <Typography variant="body2">
              <FormattedMessage id="portfolio.rentalIncome.targetRent" />
            </Typography>
          )}
        </CellBodyTypography>
      );
    },
    size: 100,
  },
  {
    header: () => <FormattedMessage id="your-dividends" defaultMessage="Your Dividends" />,
    id: 'rentalIncome',
    accessorFn: (row) => row.rentalIncome.totalDividendsPaid,
    sortingFn: (rowA, rowB) =>
      rowA.original.rentalIncome.totalDividendsPaid - rowB.original.rentalIncome.totalDividendsPaid,
    cell: ({
      row: {
        original: {
          id,
          isUpdatedRecently,
          rentalIncome: { latestDividend, totalDividendsPaid, annualizedDividendPercent },
        },
      },
    }) => (
      <CellBodyTypography>
        {latestDividend === null ? (
          <Stack>
            <Box>
              <FirstDividendDate offeringId={id} />
            </Box>
            <Box>
              <FormattedMessage id="first-dividend-date" defaultMessage="First Dividend" />
            </Box>
          </Stack>
        ) : (
          <>
            <Box display="flex" gap={1} alignItems="center">
              <PrivacyContextAwareMoneyValue length={5} value={totalDividendsPaid} />
              {isUpdatedRecently ? (
                <Chip
                  size="small"
                  label={<FormattedMessage id="updated" />}
                  sx={{
                    backgroundColor: ({ palette }) => palette.background.iceGray,
                    color: ({ palette }) => palette.text.primary,
                    textTransform: 'capitalize',
                  }}
                />
              ) : (
                <RentalIncomePercentMetric value={annualizedDividendPercent} latestDividend={latestDividend} />
              )}
            </Box>
            {isUpdatedRecently && (
              <RentalIncomePercentMetric value={annualizedDividendPercent} latestDividend={latestDividend} />
            )}
            {SHOW_NEXT_DIVIDEND_DATE && (
              <Typography variant="body2">
                <FormattedMessage id="portfolio.nextDividend" values={{ date: FORMATTED_NEXT_DIVIDEND_DATE }} />
              </Typography>
            )}
          </>
        )}
      </CellBodyTypography>
    ),
    size: 150,
  },
  {
    header: () => (
      <PortfolioEquityTooltip titleTypography={{ fontSize: 'inherit' }}>
        <FormattedMessage id="equity" defaultMessage="Equity" />
      </PortfolioEquityTooltip>
    ),
    accessorKey: 'totalValue',
    sortingFn: (rowA, rowB) => rowA.original.totalValue - rowB.original.totalValue,
    cell: ({ getValue, row }) => {
      const {
        original: { appreciation, id },
      } = row;

      return (
        <>
          <CellBodyTypography fontWeight="bold">
            <PrivacyContextAwareMoneyValue length={5} value={getValue() as number} />
          </CellBodyTypography>
          <EquityMetric
            appreciationInfo={Constants.sharePriceUpdatedOfferings.includes(id) ? appreciation : null}
            enableAwaitingValuationTooltip
          />
        </>
      );
    },
    size: 140,
  },
];

export interface OfferingTableProps {
  accountOfferingBalances: AccountOfferingBalance[];
  className?: string;
  topOffset?: number;
}

export const OfferingTable = ({ accountOfferingBalances, className, topOffset }: OfferingTableProps) => {
  const [selectedOfferingId, setSelectedOfferingId] = useState<OfferingId>();

  const accountOfferingBalance = useMemo(
    () => accountOfferingBalances.find((accountOfferingBalance) => accountOfferingBalance.id === selectedOfferingId),
    [accountOfferingBalances, selectedOfferingId]
  );
  const tableData = useMemo(
    (): OfferingColumnType[] =>
      accountOfferingBalances.map((accountOfferingBalance) => {
        const allDividends = accountOfferingBalance.rentalIncome.allDividends;
        const latestDividendPostAt = allDividends.length && allDividends[allDividends.length - 1].postedAt;
        let latestDividendPostDate: Date | null = null;
        let isUpdatedRecently = false;
        if (latestDividendPostAt) {
          latestDividendPostDate = parseISO(latestDividendPostAt);
          const updatedRecentlyCutoff = addWeeks(latestDividendPostDate, 2);
          isUpdatedRecently = isWithinInterval(CURRENT_DATE, {
            start: latestDividendPostDate,
            end: updatedRecentlyCutoff,
          });
        }
        return {
          ...accountOfferingBalance,
          ownershipStartDateParsed: new Date(accountOfferingBalance.ownershipStartDate),
          latestDividendPostDate,
          isUpdatedRecently,
        };
      }),
    [accountOfferingBalances]
  );

  const [sortState, setSortState] = useState<SortingState>([]);
  const table = useReactTable<OfferingColumnType>({
    columns,
    data: tableData,

    state: {
      sorting: sortState,
    },
    onSortingChange: setSortState,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // TODO: Determine if we want to start with an initialSort, and if so what column
    // initialState: {
    //   sortBy: [
    //     {
    //       id: 'rentalIncome',
    //       desc: true,
    //     },
    //   ],
    // },
  });

  // useEffect(() => {
  //   allColumns.filter((column) => column.id === 'view').forEach((column) => column.toggleHidden(!viewColumnShowing));
  // }, [viewColumnShowing]);

  // TODO fix typings to useCallback
  // const getRowProps: RowPropGetter<AccountOfferingBalance> = (row, meta) => {
  //   return { ...row, onClick: () => setSelectedOfferingId(meta.row.original.id) };
  // };

  const handleRowClick = useCallback(
    (row: Row<OfferingColumnType>) => setSelectedOfferingId(row.original.id),
    [setSelectedOfferingId]
  );

  const { offset, transition } = useContext(NavigationBarContext);
  return (
    <>
      <OfferingDialog setSelectedOfferingId={setSelectedOfferingId} accountOfferingBalance={accountOfferingBalance} />
      <StyledOfferingTableContainer
        sx={{
          minWidth: ({ breakpoints }) => breakpoints.values.lg + 200,
          '& .MuiTableHead-root': {
            backgroundColor: ({ palette }) => palette.background.iceGray,
            position: 'sticky',
            top: offset + (topOffset ?? 0),
            zIndex: 'mobileStepper',
            transition: `top ${transition}`,
          },
        }}
      >
        <Table className={className} cellClassName="offering-table-cell" table={table} onRowClick={handleRowClick} />
      </StyledOfferingTableContainer>
    </>
  );
};
